import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { RegistryEntityAddDialog, useRegistryCreatePermission } from '../../components/SharedRecord/RegistryEntityPicker';
import { SharedCompanyPicker } from '../../components/SharedRecord/SharedCompanyPicker';
import { useDebounce } from '../../hooks/useDebounce';
import { GovernmentOrgPicker } from '../Delivery/_shared/components/GovernmentOrgPicker';
import { useTenantId } from '../Delivery/_shared/hooks/useTenantId';
import { contactHooks, contractVehicleHooks } from '../Delivery/Registries/hooks';
/** Graph selection owns an attachment draft; registries own all identities. */ export function GraphIdentityPicker(props) {
    if (props.nodeType === 'company') {
        return /*#__PURE__*/ _jsx(SharedCompanyPicker, {
            value: props.value,
            onSelect: props.onSelect
        });
    }
    if (props.nodeType === 'gov_org') {
        return /*#__PURE__*/ _jsx(GovernmentOrgPicker, {
            id: "graph-government-org",
            label: "Government organization",
            value: props.value,
            onChange: (id)=>props.onSelect(id, 'Government organization'),
            testid: Testids.captureGraph.registryOrgPicker,
            helpText: "Select a registry organization, or add an office under its parent."
        });
    }
    return /*#__PURE__*/ _jsx(ContactVehiclePicker, {
        ...props
    }, props.nodeType);
}
function ContactVehiclePicker({ nodeType, value, onSelect }) {
    const tenant = useTenantId();
    const contact = nodeType === 'person';
    const canCreate = useRegistryCreatePermission(contact ? 'CONTACT' : 'VEHICLE');
    const [text, setText] = useState('');
    // GRW-37: creates go through RegistryEntityPicker's add dialog (email /
    // contract number first, then REG-54 ResolveEntities); never a name-only create.
    const [adding, setAdding] = useState(false);
    const term = useDebounce(text.trim(), 200);
    const contacts = contactHooks.useList({
        companyId: tenant,
        search: term
    }, {
        enabled: contact && !!term
    });
    const vehicles = contractVehicleHooks.useList({
        companyId: tenant
    }, {
        enabled: !contact && !!term
    });
    const query = contact ? contacts : vehicles;
    const results = contact ? (contacts.data?.items ?? []).map((c)=>({
            id: c.id,
            name: `${c.firstName} ${c.lastName}`.trim(),
            identity: [
                c.title,
                c.email,
                c.organization
            ].filter(Boolean).join(' · ')
        })) : (vehicles.data?.items ?? []).filter((v)=>v.name.toLowerCase().includes(term.toLowerCase())).map((v)=>({
            id: v.id,
            name: v.name,
            identity: [
                v.vehicleType,
                v.contractNumber
            ].filter(Boolean).join(' · ')
        }));
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        "data-testid": Testids.captureGraph.registryPicker,
        children: [
            /*#__PURE__*/ _jsx(SpecraInput, {
                "aria-label": "Search registry",
                placeholder: contact ? 'Search registry contacts…' : 'Search registry vehicles…',
                value: text,
                onChange: (e)=>setText(e.target.value),
                "data-testid": Testids.captureGraph.registrySearch
            }),
            query.isFetching && /*#__PURE__*/ _jsx("output", {
                children: "Searching registry…"
            }),
            query.isError && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    "Registry search unavailable. Your draft is kept.",
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>query.refetch(),
                        children: "Retry search"
                    })
                ]
            }),
            term && !query.isError && results.map((r)=>/*#__PURE__*/ _jsxs(SpecraButton, {
                    variant: "secondary",
                    "data-testid": Testids.captureGraph.registryResult,
                    "data-record-id": r.id,
                    onClick: ()=>onSelect(r.id, r.name),
                    children: [
                        r.name,
                        " · ",
                        contact ? 'Contact' : 'Vehicle',
                        " · ",
                        r.identity
                    ]
                }, r.id)),
            value && /*#__PURE__*/ _jsx("p", {
                "data-testid": Testids.captureGraph.registrySelected,
                "data-record-id": value,
                children: "Registry record selected"
            }),
            canCreate && term && !query.isError && !query.isFetching && term === text.trim() && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border p-2",
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs",
                        children: [
                            "Not in the results? Add a shared ",
                            contact ? 'Contact' : 'Vehicle',
                            " to Registries.",
                            contact ? ' With an email we can match an existing person.' : ' The contract number identifies it.'
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        "data-testid": Testids.captureGraph.registryCreate,
                        disabled: !!value,
                        onClick: ()=>setAdding(true),
                        children: [
                            "Add ",
                            contact ? 'Contact' : 'Vehicle'
                        ]
                    })
                ]
            }),
            adding && /*#__PURE__*/ _jsx(RegistryEntityAddDialog, {
                kind: contact ? 'CONTACT' : 'VEHICLE',
                initialText: text,
                onPick: (o)=>onSelect(o.registryId, o.label),
                onClose: ()=>setAdding(false)
            })
        ]
    });
}
